import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      switch (user.role) {
        case 'super_admin':
        case 'admin': navigate('/dashboard/admin'); break;
        case 'merchant': navigate('/dashboard/merchant'); break;
        case 'internal': navigate('/dashboard/internal'); break;
        case 'buyer': navigate('/dashboard/buyer'); break;
        default: navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Media Panel */}
      <div className="hidden lg:flex lg:w-[50%] login-media-panel relative grain-overlay">
        {/* Factory Image */}
        <div className="absolute inset-0 z-0">
          <img src="/factory-day.jpg" alt="Paridhi Apparels Factory" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>

        {/* Bottom text overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-12 z-20 bg-gradient-to-t from-black/60 via-black/20 to-transparent">
          <p className="overline">Real-Time Tracking</p>
          <h2 className="font-heading text-3xl font-semibold text-white mt-4 tracking-tight leading-[1.15]">
            Complete visibility into<br />your production cycle
          </h2>
          <p className="text-sm text-white/35 mt-4 leading-relaxed">Paridhi Apparels Pvt. Ltd.</p>
        </div>
      </div>

      {/* Right: Login Form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 bg-white">
        <div className="w-full max-w-sm mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#71717A] hover:text-[#111111] mb-14 transition-colors duration-300"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to website
          </Link>

          <div className="flex items-center gap-3 mb-14">
            <img src="/logo-icon.png" alt="Paridhi Apparels" className="h-10 w-auto" />
            <div>
              <h2 className="font-heading text-lg font-semibold tracking-tight logo-text-animated">Paridhi Apparels</h2>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-medium">Manufacturing Portal</p>
            </div>
          </div>

          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#111111] tracking-tight leading-[1.15]">
              Production Transparency.<br />Simplified.
            </h1>
            <p className="text-sm text-[#52525B] mt-4 leading-relaxed">
              Access your orders, track real-time progress, and stay informed at every stage of production.
            </p>
          </div>

          <form onSubmit={handleLogin} className="mt-12 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[11px] font-semibold tracking-[0.15em] uppercase text-[#71717A]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="h-12 rounded-xl border-black/[0.08] focus:border-[#111111] focus:ring-[#111111]/10 bg-[#FAFAF8] transition-all"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[11px] font-semibold tracking-[0.15em] uppercase text-[#71717A]">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="h-12 rounded-xl border-black/[0.08] focus:border-[#111111] focus:ring-[#111111]/10 pr-10 bg-[#FAFAF8] transition-all"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#111111] transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#111111] hover:bg-black text-white rounded-xl text-sm font-semibold tracking-wide btn-premium"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Login to Dashboard'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
